import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { getStripeCardStatus } from "~/api/stripe/getStatus";
import { setupIntent } from "~/api/stripe/subscribe";
import MainButton from "~/components/common/mainButton";
import Modal from "~/components/ui/modal";
import { StripeCardStatus } from "~/types/stripe";
import { openUrl } from "~/utils/functions/dom.functions";

import styles from "./index.module.scss";

type Props = {
  open: boolean;
  handleClose: () => void;
};

const PaymentForm = ({ open, handleClose }: Props) => {
  const queryClient = useQueryClient();
  const [cardStatus, setCardStatus] = useState<StripeCardStatus>("NONE");
  const [error, setError] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [canClose, setCanClose] = useState(false);

  const { mutateAsync, isLoading } = useMutation(setupIntent, {
    onSuccess: (resp) => {
      const url = resp?.next_action?.redirect_to_url?.url;
      if (url) openUrl(url);
      const sdk = resp?.next_action?.use_stripe_sdk?.stripe_js;
      if (sdk) openUrl(sdk);
      setCanClose(true);
    },
    onError: () => {
      setError("Une erreur est survenue, merci de réessayer.");
      setSubmitted(false);
    },
  });

  const StripeElementOptions = useMemo(
    () => ({
      style: {
        base: {
          cursor: "pointer",
          color: "#fff",
          "::placeholder": {
            color: "grey",
          },
        },
        invalid: {
          color: "#c23d4b",
        },
      },
    }),
    []
  );

  useEffect(() => {
    if (cardStatus === "ACCEPTED") {
      if (canClose) {
        setTimeout(() => {
          queryClient.invalidateQueries(["user"]);
        }, 5000);
        handleClose();
      }
    }
    if (cardStatus === "REFUSED")
      setError("Votre paiement a été refusé, merci d'essayer à nouveau.");
  }, [cardStatus, canClose, queryClient, submitted, handleClose]);

  useEffect(() => {
    const interval = setInterval(() => {
      getStripeCardStatus().then((res) => {
        setCardStatus(res.stripeCardStatus);
      });
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
    if (elements && stripe) {
      const cardNumberElement = elements.getElement("cardNumber");

      if (cardNumberElement) {
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
        });
        if (paymentMethod && paymentMethod?.card) {
          await mutateAsync({
            paymentMethod: paymentMethod.id,
            stripeLast4Digit: paymentMethod.card.last4,
          });
          setSubmitted(true);
        }
      }
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={styles.container}>
        <p>Veuillez entrer les détails de paiement ci-dessous</p>
        <div className={styles.formContainer}>
          <CardNumberElement
            className={styles.cardInput}
            options={StripeElementOptions}
          />

          <CardExpiryElement
            className={styles.cardInput}
            options={StripeElementOptions}
          />

          <CardCvcElement
            className={styles.cardInput}
            options={StripeElementOptions}
          />
          <MainButton
            isLoading={isLoading}
            onClick={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            Confirmer
          </MainButton>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      </div>
    </Modal>
  );
};

const PaymentModal = ({ open, handleClose }: Props) => {
  const key = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_API;
  const stripePromise = loadStripe(key ?? "");

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm open={open} handleClose={handleClose} />
    </Elements>
  );
};

export default PaymentModal;
